import { useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import { CompaniesService } from 'api/services/CompaniesService';
import {
  COMPANY_KEY_DEVELOPMENTS_DEFAULT_ERROR_MESSAGE,
  COMPANY_KEY_DEVELOPMENTS_QUERY_KEY,
} from 'common/constants/services/companies';

// Each category is fetched and cached independently so switching combinations
// never refetches data that was already loaded.
const useGetCompanyKeyDevelopments = (companyId: number | undefined, categories: string[]) => {
  const { enqueueSnackbar } = useSnackbar();

  const results = useQueries({
    queries: categories.map(cat => ({
      enabled: !isNil(companyId),
      queryFn: () => CompaniesService.apiCompaniesKeyDevelopmentsRead(companyId as number, [cat]),
      queryKey: [COMPANY_KEY_DEVELOPMENTS_QUERY_KEY, companyId, cat],
      retry: false,
      staleTime: Infinity,
      gcTime: 0,
    })),
  });

  const isLoading = results.some(r => r.isLoading);
  const isFetching = results.some(r => r.isFetching);
  const isError = results.some(r => r.isError);
  const items = results.flatMap(r => r.data?.results ?? []);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(COMPANY_KEY_DEVELOPMENTS_DEFAULT_ERROR_MESSAGE, { variant: 'error' });
    }
  }, [isError, enqueueSnackbar]);

  return { items, isLoading, isFetching, isError };
};

export default useGetCompanyKeyDevelopments;
